import fs from "node:fs/promises";
import path from "node:path";
import type { NoriPaths } from "@mewbleh/nori-persistence";
import type { NoriConfig } from "./config.js";

export const BUNDLED_HOOKS = [
  { name: "boot-md", emoji: "🚀", description: "Run BOOT.md through each configured agent on Gateway startup.", events: ["gateway:startup"] },
  { name: "bootstrap-extra-files", emoji: "📎", description: "Inject explicitly configured workspace files into agent bootstrap context.", events: ["agent:bootstrap"] },
  { name: "command-logger", emoji: "📝", description: "Write redacted Gateway command events to logs/commands.log.", events: ["command"] },
  { name: "compaction-notifier", emoji: "🧹", description: "Emit session compaction before/after lifecycle events.", events: ["session:compact:before", "session:compact:after"] },
  { name: "session-memory", emoji: "💾", description: "Save recent session context to workspace memory before reset.", events: ["command:reset"] },
] as const;

export type BundledHookName = (typeof BUNDLED_HOOKS)[number]["name"];

export function hookEnabled(config: NoriConfig, name: BundledHookName): boolean {
  return config.hooks.internal.enabled && config.hooks.internal.entries[name]?.enabled === true;
}

export function listHooks(config: NoriConfig) {
  return BUNDLED_HOOKS.map((hook) => ({ ...hook, source: "bundled", eligible: true, enabled: hookEnabled(config, hook.name) }));
}

export function setHookEnabled(config: NoriConfig, name: string, enabled: boolean): void {
  if (!BUNDLED_HOOKS.some((hook) => hook.name === name)) throw new Error(`Unknown bundled hook: ${name}`);
  config.hooks.internal.entries[name] = { ...config.hooks.internal.entries[name], enabled };
  if (enabled) config.hooks.internal.enabled = true;
}

function redact(value: unknown, depth = 0): unknown {
  if (depth > 8) return "[truncated]";
  if (Array.isArray(value)) return value.slice(0, 100).map((entry) => redact(entry, depth + 1));
  if (!value || typeof value !== "object") return typeof value === "string" && value.length > 2_048 ? `${value.slice(0, 2_048)}…` : value;
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).slice(0, 100).map(([key, entry]) => [key, /token|secret|password|api.?key|authorization|credential/iu.test(key) ? "[REDACTED]" : redact(entry, depth + 1)]));
}

export async function appendCommandLog(paths: NoriPaths, event: { method: string; params: unknown; role: string }): Promise<void> {
  await fs.mkdir(paths.logsDir, { recursive: true, mode: 0o700 });
  await fs.appendFile(path.join(paths.logsDir, "commands.log"), `${JSON.stringify({ ts: Date.now(), method: event.method, role: event.role, params: redact(event.params) })}\n`, { mode: 0o600 });
}

export async function saveSessionMemory(workspace: string, sessionKey: string, messages: Array<{ role: string; content: unknown; createdAt: number }>, limit = 15): Promise<string | undefined> {
  if (!messages.length) return undefined;
  const directory = path.join(workspace, "memory");
  await fs.mkdir(directory, { recursive: true, mode: 0o700 });
  const stamp = new Date().toISOString().replaceAll(":", "-").replace(".", "-");
  const filePath = path.join(directory, `${stamp}-session.md`);
  const body = [`# Session memory`, "", `Session: ${sessionKey}`, "", ...messages.slice(-Math.max(1, Math.min(limit, 200))).flatMap((message) => [`## ${message.role}`, "", typeof message.content === "string" ? message.content : JSON.stringify(message.content), ""])].join("\n");
  await fs.writeFile(filePath, `${body.trim()}\n`, { flag: "wx", mode: 0o600 });
  return filePath;
}

export async function loadExtraBootstrapFiles(workspace: string, config: NoriConfig): Promise<Array<{ path: string; content: string }>> {
  if (!hookEnabled(config, "bootstrap-extra-files")) return [];
  const patterns = config.hooks.internal.entries["bootstrap-extra-files"]?.paths ?? [];
  const root = await fs.realpath(workspace);
  const found = new Set<string>();
  for (const pattern of patterns) {
    if (path.isAbsolute(pattern) || pattern.split(/[\\/]/u).includes("..")) throw new Error(`Unsafe bootstrap-extra-files pattern: ${pattern}`);
    for await (const relative of fs.glob(pattern, { cwd: root, withFileTypes: false })) found.add(relative);
  }
  const files: Array<{ path: string; content: string }> = [];
  for (const relative of [...found].sort().slice(0, 50)) {
    const candidate = await fs.realpath(path.join(root, relative));
    if (candidate !== root && !candidate.startsWith(`${root}${path.sep}`)) throw new Error(`Bootstrap file escapes workspace: ${relative}`);
    const stat = await fs.stat(candidate);
    if (!stat.isFile() || stat.size > 128 * 1024) throw new Error(`Bootstrap file is not a bounded regular file: ${relative}`);
    files.push({ path: relative, content: await fs.readFile(candidate, "utf8") });
  }
  return files;
}
